import type { Rule, RuleGroup } from '@/api/types';

/**
 * Canonical-leaf representation used at the boundary between the visual rule
 * editor and the API. The visual editor may keep labels and picker metadata in
 * its own state, but it must resolve those values to a typed `Rule` before
 * handing a group to this codec.
 */
export interface UiAudienceAndGroup {
  /** Stable UI identity; also identifies this branch in per-segment reports. */
  id: string;
  label?: string;
  op: 'AND';
  rules: Rule[];
}

export interface ReportableAudienceSegment {
  id: string;
  label: string;
  /** Zero-based top-level branch in the canonical OR tree. */
  ordinal: number;
  /** An isolated queryable definition for this segment. */
  rules: RuleGroup;
}

export interface EncodedAudienceRules {
  /** Overall audience: segment 1 OR segment 2 OR … */
  rules: RuleGroup;
  /** Each UI group retained as an independent all-of report segment. */
  segments: ReportableAudienceSegment[];
}

export class AudienceRuleCodecError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AudienceRuleCodecError';
  }
}

function segmentId(id: string, ordinal: number): string {
  const clean = id.trim();
  return clean || `segment-${ordinal + 1}`;
}

/**
 * Encode visual AND groups without flattening them.
 *
 * A root `any` group preserves the visual OR dividers. Every child remains a
 * nested `all` group, which means it can also be previewed/reported by sending
 * the corresponding `segments[n].rules` definition on its own.
 */
export function encodeAudienceRuleGroups(
  groups: readonly UiAudienceAndGroup[],
): EncodedAudienceRules {
  if (groups.length === 0) {
    throw new AudienceRuleCodecError('At least one audience segment is required');
  }

  const ids = new Set<string>();
  const segments = groups.map((group, ordinal): ReportableAudienceSegment => {
    if (group.op !== 'AND') {
      throw new AudienceRuleCodecError(`Segment ${ordinal + 1} must use AND`);
    }
    if (group.rules.length === 0) {
      throw new AudienceRuleCodecError(`Segment ${ordinal + 1} must contain at least one rule`);
    }

    const id = segmentId(group.id, ordinal);
    if (ids.has(id)) {
      throw new AudienceRuleCodecError(`Duplicate audience segment id: ${id}`);
    }
    ids.add(id);

    return {
      id,
      label: group.label?.trim() || `Segment ${ordinal + 1}`,
      ordinal,
      rules: {
        op: 'all',
        rules: [...group.rules],
      },
    };
  });

  return {
    rules: {
      op: 'any',
      rules: segments.map((segment) => ({
        kind: 'group',
        ...segment.rules,
      })),
    },
    segments,
  };
}

/**
 * Decode persisted canonical rules into reportable visual segments.
 *
 * New definitions produced by `encodeAudienceRuleGroups` round-trip exactly.
 * Older definitions with a root `all` become one segment. A legacy root `any`
 * with unwrapped leaves becomes one single-rule segment per OR branch, keeping
 * its meaning while normalizing the next save to explicit AND groups.
 */
export function decodeAudienceRuleGroups(
  root: RuleGroup,
  ids: readonly string[] = [],
): UiAudienceAndGroup[] {
  const branches: Rule[][] = root.op === 'all'
    ? [[...root.rules]]
    : root.rules.map((rule) =>
      rule.kind === 'group' && rule.op === 'all'
        ? [...rule.rules]
        : [rule],
    );

  return branches.map((rules, ordinal) => ({
    id: segmentId(ids[ordinal] ?? '', ordinal),
    op: 'AND',
    rules,
  }));
}

/** Return independent all-of definitions for preview/report fan-out. */
export function reportableAudienceSegments(
  root: RuleGroup,
  ids: readonly string[] = [],
): ReportableAudienceSegment[] {
  return decodeAudienceRuleGroups(root, ids).map((group, ordinal) => ({
    id: group.id,
    label: `Segment ${ordinal + 1}`,
    ordinal,
    rules: { op: 'all', rules: [...group.rules] },
  }));
}
